export const RETRYABLE_SAVE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

type InvestorSubmitErrorLike = {
  code?: unknown;
  message?: unknown;
  name?: unknown;
  status?: unknown;
  response?: {
    data?: {
      detail?:
        | string
        | {
            message?: unknown;
            type?: unknown;
          };
      message?: unknown;
    };
    headers?: Record<string, unknown>;
    status?: unknown;
  };
};

export const createRequestIdentifier = (prefix: string): string => {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (typeof randomUUID === 'function') {
    return `${prefix}-${randomUUID.call(globalThis.crypto)}`;
  }

  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const waitForInvestorSubmitRetry = (delayMs: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, delayMs));

export const getInvestorSubmitErrorStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const maybeError = error as InvestorSubmitErrorLike;
  if (typeof maybeError.response?.status === 'number') {
    return maybeError.response.status;
  }
  if (typeof maybeError.status === 'number') {
    return maybeError.status;
  }

  return null;
};

export const getInvestorSubmitErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const maybeError = error as InvestorSubmitErrorLike;
  if (typeof maybeError.code === 'string' && maybeError.code.trim()) {
    return maybeError.code;
  }
  if (typeof maybeError.name === 'string' && maybeError.name.trim()) {
    return maybeError.name;
  }

  return null;
};

export const getInvestorSubmitResponseDetailType = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const detail = (error as InvestorSubmitErrorLike).response?.data?.detail;
  if (detail && typeof detail === 'object' && typeof detail.type === 'string') {
    return detail.type;
  }

  return null;
};

export const getInvestorSubmitResponseRequestId = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const headers = (error as InvestorSubmitErrorLike).response?.headers;
  const requestId =
    headers?.['x-request-id'] ??
    headers?.['X-Request-Id'] ??
    headers?.['x-amzn-requestid'] ??
    headers?.['x-amz-request-id'];

  return typeof requestId === 'string' && requestId.trim() ? requestId : null;
};

export const getInvestorSubmitErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    const maybeError = error as InvestorSubmitErrorLike;
    const detail = maybeError.response?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (detail && typeof detail === 'object' && typeof detail.message === 'string') {
      return detail.message;
    }
    if (typeof maybeError.response?.data?.message === 'string') {
      return maybeError.response.data.message;
    }
    if (typeof maybeError.message === 'string') {
      return maybeError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }

  return '';
};

export const sanitizeFingerprintPart = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);

export const hashText = (value: string): string => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash.toString(36);
};
